import { forwardRef } from "react";
import { CoreButton } from "@boi/components-core";
import { useDesignTokens } from "@boi/react-design-tokens";

import * as S from "./Button.styled";
import ButtonBase, { type ButtonBaseProps } from "./ButtonBase";
import { Typography } from "../Typography";
import { Spinner } from "../Spinner";

export type ButtonProps = Omit<
  ButtonBaseProps,
  "variant" | "children" | "testID"
> & {
  text: string;
  variant?: CoreButton.Variant;
  startIcon?: JSX.Element;
  isLoading?: boolean;
  testID?: string;
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    text,
    variant = CoreButton.DEFAULT_VARIANT,
    startIcon,
    isLoading,
    testID = "button",
    ...buttonProps
  } = props;

  const { tokens } = useDesignTokens();
  const { button } = tokens;

  const isLoadingProps = {
    "aria-disabled": isLoading ? true : undefined,
    "aria-label": isLoading ? "Loading" : undefined,
  };

  return (
    <ButtonBase
      {...buttonProps}
      {...isLoadingProps}
      ref={ref}
      variant={variant}
      isLoading={isLoading}
      testID={testID}
    >
      <S.Container>
        {startIcon && (
          <S.IconWrapper $isLoading={isLoading}>{startIcon}</S.IconWrapper>
        )}

        <S.TypographyWrapper $isLoading={isLoading}>
          <Typography
            variant={button.topLayer.text.typography.variant}
            fontFamily={button.topLayer.text.typography.fontFamily}
            noWrap
            color="inherit"
            component="div"
            testID={`${testID}-text`}
          >
            {text}
          </Typography>
        </S.TypographyWrapper>

        {isLoading && (
          <S.SpinnerWrapper>
            <Spinner
              aria-label="Loading..."
              variant={CoreButton.SPINNER_VARIANTS[variant]}
              testID={`${testID}-spinner`}
            />
          </S.SpinnerWrapper>
        )}
      </S.Container>
    </ButtonBase>
  );
});

Button.displayName = "Button";

export default Button;
